/**
 * @file Poppy - Creates simple eye candy JavaScript notification
 * @version v1.2.0
 * @license MIT license - http://opensource.org/licenses/MIT
 * @author Berat Aşıcı (https://berat.me)
 */
 export class Poppy {

    constructor(message, title = null, options = {}) {
        this.setOptions(message, title, options);
        this.init();
    }

    show() {
        if (!this.flags.isGenerated) {
            this.generateNotification();
            this.registerObserver();
        }

        if (!this.flags.hasRegisteredDragEvents) {
            this.registerDragEvents();
        }

        this.registerDismissal();
        // Set Positioning
        this.setPositionForAnimation();
        this.flags.hasDismissed = false;
        this.flags.isVisible = true;

        this.repositionAll();
    }

    dismiss() {
        const self = this;
        if (self.options.element) {
            self.options.element.setAttribute('poppy-state', 'dismissing');
            this.options.element.classList.add(this.class('isDismissing'));
            this.emit('dismissing', [self]);

            let dismissDuration = this.getDismissDuration();
            setTimeout(function() {
                if (!self.flags.hasDismissed) {
                    self.options.wrapper.removeChild(self.options.element);
                    self.repositionAll();
                    self.options.element.classList.remove(self.class('isDismissing'));
                    self.options.element.removeAttribute('poppy-state');
                    self.emit('dismissed', [self]);
                    self.flags.hasDismissed = true;
                    self.flags.isVisible = false;
                }
            }, dismissDuration);
        }
    }

    repositionAll() {
        let y = 1;
        let x = 0;
        let samePositionNotifications = Array.from(document.getElementsByClassName(this.getPositionClass()));

        if (this.positioning.bottom) {
            samePositionNotifications = samePositionNotifications.reverse();
        }

        if (this.positioning.top || this.positioning.bottom) {
            y += this.options.offset.y;
        }

        if (this.positioning.left || this.positioning.right) {
            x += this.options.offset.x;
        }

        if (this.positioning.center) {
            x = '-50%';
        }

        for (let i = 0; i < samePositionNotifications.length; i++) {
            // Discard ongoing dragging
            if (samePositionNotifications[i].getAttribute('poppy-state') == 'dragging')  {
                samePositionNotifications[i].setAttribute('poppy-state', 'discard');
            }

            const self = this;
            setTimeout(function() {
                let notification = samePositionNotifications[i];
                notification.classList.remove(self.class('isInvisible'));
                notification.classList.remove(self.class('hasNoAnimation'));
                notification.style.transform = `translate(${x}, ${self.positioning.bottom ? 0 - y : y}px)`;

                y += notification.offsetHeight;

                if (self.positioning.bottom) {
                    y += parseFloat(window.getComputedStyle(notification).marginTop);
                } else {
                    y += parseFloat(window.getComputedStyle(notification).marginBottom);
                }
            }, 1);
        }
    }

    get positioning() {
        if (this.options.__positioning === undefined) {
            let positioningClass = this.getPositionClass();

            this.options.__positioning = Object.fromEntries(
                ['top', 'bottom', 'left', 'right', 'center'].map(
                    key => [key, positioningClass.indexOf(key) !== -1]
                    )
                );
        }

        return this.options.__positioning;
    }

    progress(value) {
        let progressbarLine = this.options.element.getElementsByClassName(this.class('progressbarLine'))[0];
        
        if (progressbarLine) {
            const percentage = this.calculateProgressbarWidth(value);
            progressbarLine.style.width =  percentage + '%';
            
            this.emit('progress', [this, percentage, value]);
        } else {
            console.error('Progressbar could not be found in notification.');
            return;
        }

        return this;
    }

    calculateProgressbarWidth(value, min, max) {
        min = this.options.progressbar.min || this.defaults().progressbar.min;
        max = this.options.progressbar.max || this.defaults().progressbar.max;

        return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
    }

    init() {
        this.registerEvents();
        this.registerFlags();
        this.generateWrapper();
    }

    generateWrapper() {
        let wrapper = this.options.wrapper || document.getElementsByClassName(this.options.style.classNames.wrapper)[0];

        if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = this.options.style.classNames.wrapper;
            this.options.container.appendChild(wrapper);
        }

        this.options.wrapper = wrapper;
    }

    generateNotification() {
        let notification = this.options.element;

        if (!notification) {
            notification = document.createElement('div');
            notification.className = this.class('notification');
            
            if (this.options.progressbar.show) {
                notification.classList.add(this.class('hasProgressbar'));
            }

            // Set Position Class
            notification.classList.add(this.getPositionClass());

            notification.classList.add(this.class('notification') + this.class('colors')[this.options.color]);

            notification.innerHTML = `
            <div class="${this.class('body')}">
                <div class="${this.class('icon')}">${this.getIcon()}</div>
                <section class="${this.class('content')}">
                    <div class="${this.class('dismissButton')}">${this.icons('dismiss')}</div>
                    <header class="${this.class('title')}">${this.options.title}</header>
                    <article class="${this.class('message')}">${this.options.message}</article>
                    <footer class="${this.class('footer')}"></footer>
                </section>
            </div>
            <div class="${this.class('progressbar')}"><div class="${this.class('progressbarLine')}"></div></div>
            `;
            
/*            <div class="${this.class('buttonContainer')}">
                <button class="${this.class('button')} ${this.class('buttonPrimary')}">Accept</button>
                <button class="${this.class('button')} ${this.class('buttonSecondary')}">Dismiss</button>
            </div> */
    
        }

        const self = this;
        notification.onclick = function(event) {
            return self.handleClickEvent(self, event);
        }

        this.options.element = notification;
        this.flags.isGenerated = true;
    }

    getCurrentNotificationMatrix() {
        let matrixKeys = ['scalex', 'skewy', 'skewx', 'scaley', 'translatex', 'translatey'];
        let matrix = window.getComputedStyle(this.options.element).transform.match(/-?\d+\.?\d*(\e\-\d+)?/g);
        return Object.fromEntries(matrix.map((_, i) => [matrixKeys[i], parseFloat(_)]));
    }

    matrixToTransformString(matrix) {
        return `matrix(1, 0, 0, 1, ${matrix.translatex}, ${matrix.translatey})`;
        return `scale(${matrix.scalex}, ${matrix.scaley}) skew(${matrix.skewx}deg, ${matrix.skewy}deg) translate(${matrix.translatex}px, ${matrix.translatey}px)`;
        // return `matrix(${matrix.scalex}, ${matrix.skewy}, ${matrix.skewx}, ${matrix.scaley}, ${matrix.translatex}, ${matrix.translatey})`;
    }

    setPositionForAnimation() {
        let y = 0;
        let x = 0;
        
        this.options.element.classList.add(this.class('isInvisible'));
        this.options.element.classList.add(this.class('hasNoAnimation'));
        
        document.body.appendChild(this.options.element);

        let style = window.getComputedStyle(this.options.element);

        if (this.positioning.center && this.positioning.bottom) {
            y = this.options.element.offsetHeight;
        } else if (this.positioning.center && this.positioning.top) {
            y = 0 - this.options.element.offsetHeight;
        }

        if (this.positioning.right) {
            x = this.options.element.offsetWidth + parseFloat(style.right);
        } else if (this.positioning.left) {
            x = 0 - this.options.element.offsetWidth - parseFloat(style.left);
        }

        if (this.positioning.center) {
            x = '-50%';
        } else {
            x = x + 'px';
        }
        
        this.options.element.style.transform = `translate(${x}, ${y}px)`;

        if (this.positioning.bottom) {
            this.options.wrapper.appendChild(this.options.element);
        } else {
            this.options.wrapper.insertBefore(this.options.element, this.options.wrapper.firstChild);
        }
    }

    getIcon() {
        let icon = this.options.icon || false;

        if (icon.length) {
            // If defined icon is in URL format generate an IMG element with given URL
            if (icon.indexOf('http') === 0 || icon.indexOf('/') === 0) {
                return `<img src="${icon}">`;
            }

            // If icon is an HTML element such as SVG or DIV return it as it is
            if (icon.indexOf('<') === 0) {
                return icon;
            }

            // If all above fails treat it as css font library class and generate it accordingly
            return `<i class="${icon}"></i>`

        }

        return '';
    }

    getPositionClass() {
        let positionClassSuffix = this.options.position.toLowerCase().replace(' ', '-');
        return this.class('notification') + '--' + positionClassSuffix;
    }

    getDismissDuration() {
        let transitions = Object.fromEntries(getComputedStyle(this.options.element).transition.split(', ').map(function(transition) {
            let [key, duration, curve, delay] = transition.split(' ');
            return [key, {duration: duration, curve: curve, delay: delay}]
        }));

        return parseFloat(transitions.opacity ? transitions.opacity.duration : (transitions.all ? transitions.all.duration : 0.25) ) * 1000;
    }

    set title(title) {
        const previous = this.options.title;
        this.options.title = title;

        if (this.flags.isGenerated) {
            this.options.wrapper.getElementsByClassName(this.class('title'))[0].innerText = title;
        }
        
        this.emit('titlechanged', [this, title, previous]);
    }

    get title() {
        return this.options.title;
    }
    
    set message(message) {
        const previous = this.options.message;
        this.options.message = message;

        if (this.flags.isGenerated) {
            this.options.element.getElementsByClassName(this.class('message'))[0].innerText = message;
        }
        
        this.emit('messagechanged', [this, message, previous]);
    }

    get message() {
        return this.options.message;
    }

    set icon(icon) {
        const previous = this.options.icon;
        this.options.icon = icon;
        
        if (this.flags.isGenerated) {
            this.options.wrapper.getElementsByClassName(this.class('icon'))[0].innerHTML = this.getIcon();
        }

        this.emit('iconchanged', [this, icon, this.getIcon(), previous]);
    }

    get icon() {
        return this.getIcon();
    }

    set color(color) {
        this.options.color = color;
        this.options.element.classList.remove(this.class('notification') + this.class('colors').info);
        this.options.element.classList.remove(this.class('notification') + this.class('colors').success);
        this.options.element.classList.remove(this.class('notification') + this.class('colors').warning);
        this.options.element.classList.remove(this.class('notification') + this.class('colors').error);

        this.options.element.classList.add(this.class('notification') + this.class('colors')[color]);
    }

    get color() {
        return this.options.color;
    }

    set showProgressbar(bool) {
        this.options.progressbar.show = !!bool;

        if (this.flags.isGenerated) {
            this.options.wrapper.getElementsByClassName(this.class('notification'))[0].classList.toggle(this.class('hasProgressbar'), bool);
        }

        this.emit('progressbarstatuschanged', [this, bool]);
    } 

    get showProgressbar() {
        return this.options.progressbar.show;
    }

    setContainer() {
        let selector = this.options.container || document.body;
        this.options.container = typeof selector == "string" ? document.querySelector(selector) : document.body;
    }

    defaults() {
        return this._defaults || {
            icon: null,
            title: '',
            message: '',
            url: null,
            target: '_blank',
            action: false,
            duration: 5000,
            color: 'info',
            canDismiss: true,
            position: 'top right',
            container: document.body,
            wrapper: null,
            progressbar: {
                show: false,
                max: 100,
                min: 0,
                value: 0
            },
            offset: {
                y: 0,
                x: 0
            },
            style: {
                classNames: {
                    wrapper: 'poppy',
                    notification: 'poppy__notification',
                    isInvisible: 'poppy__notification--invisible',
                    isDismissing: 'poppy__notification--dismissing',
                    hasNoAnimation: 'poppy__notification--no-animation',
                    hasProgressbar: 'poppy__notification--progressbar',
                    body: 'poppy__body',
                    content: 'poppy__content',
                    title: 'poppy__title',
                    message: 'poppy__message',
                    icon: 'poppy__icon',
                    isSpinning: 'poppy__icon--spinning',
                    footer: 'poppy__footer',
                    progressbar: 'poppy__progressbar',
                    progressbarLine: 'poppy__progressbar-line',
                    button: 'poppy__button',
                    buttonPrimary: 'poppy__button--primary',
                    buttonSecondary: 'poppy__button--secondary',
                    buttonDisabled: 'poppy__button--disabled',
                    buttonContainer: 'poppy__button-container',
                    dismissButton: 'poppy__dismiss-button',
                    colors: {
                        info: '--info',
                        success: '--success',
                        warning: '--warning',
                        error: '--error',
                    }
                },
                icons: {
                    dismiss: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23"><path d="M4.328.743l7.171 7.17 7.173-7.17a2.536 2.536 0 113.585 3.585L15.085 11.5l7.172 7.173a2.536 2.536 0 11-3.585 3.585l-7.173-7.172-7.17 7.172a2.536 2.536 0 11-3.586-3.585l7.17-7.173-7.17-7.17A2.536 2.536 0 114.328.742z"/></svg>'
                }
            }
        };
    }

    // Event Handler
    addEvent(event, options) {
        if (this.events[event] == undefined) {
            this.events[event] = [];
        }

        this.events[event].push(options);
    }

    on(event, callback) {
        this.addEvent(event, {
            listener: callback,
            once : false,
            active: true,
        });

        return this;
    }

    once(event, callback) {
        this.addEvent(event, {
            listener: callback,
            once : true,
            active: true,
        });

        return this;
    }

    emit(event, params) {
        if (Array.isArray(this.events[event])) {
            this.events[event].forEach((event) => {
                if (event.active) {
                    event.listener(...params);
                    if (event.once) {
                        event.active = false;
                    }
                }
            });
        }

        return this;
    }

    handleClickEvent(self, event) {

        self.emit('clicked', [self]);

        if(event.target.closest('.' + self.class('dismissButton'))) {
            self.dismiss();
            self.emit('dismissclick', [self]);
            return;
        }

        if (typeof self.options.action == 'function') {
            self.options.action(self);
            return;
        }

        switch(self.options.action) {
            case 'dismiss':
                self.dismiss();
                break;
            case 'link':
                window.open(this.options.url, this.options.target);
                break;
            case false:
            case 'none':
                // self.expandButtons();
                break;
        }

        return;
    }

    registerEvents() {
        this.events = {};
    }

    registerDragEvents() {
        const self = this;

        document.documentElement.addEventListener('mousedown', function(event) {
            if (event.buttons != 1 && (event.which || event.button) != 1) {
                // If it's not left click ignore it
                return;
            }

            // Try to find which notification element has clicked
            const notification = event.target.closest('.' + self.class('notification'));

            // Check if notification is generated by this class
            if (notification && notification == self.options.element && !self.options.element.getAttribute('poppy-state')) {
                // Set our flag so we won't do unnecessary calculations on mousemove
                self.flags.isMouseDown = true;

                // Set the drag starting point
                self.options.__drag = {
                    element: notification,
                    startMatrix: self.getCurrentNotificationMatrix(),
                    startX: event.clientX,
                    startY: event.clientY,
                    startTime: (new Date()).getTime()
                }

                if (self.positioning.center) {
                    self.options.element.style.zIndex = 999;

                    Array.from(document.querySelectorAll('.' + self.class('notification') + '--top-center, ' + self.class('notification') + '--bottom-center')).forEach(otherNotification => {
                        if (otherNotification !== self.options.element) {
                            otherNotification.style.zIndex = 1000;
                        }
                    })
                };

                // Remove animations on notification element so it won't interfere with our dragging
                self.options.element.classList.add(self.class('hasNoAnimation'));
            }
        }, true);

        document.documentElement.addEventListener('mousemove', function(event) {
            if (self.flags.isMouseDown) {

                // Calculate the distance according to its positioning
                let distance = self.positioning.center ? event.clientY - self.options.__drag.startY : event.clientX - self.options.__drag.startX;

                // If mouse has moved 10 pixels or more we can assume user is dragging the notification
                if(Math.abs(distance) >= 10) {
                    self.flags.isDragging = true;
                    // Set notification state
                    self.options.element.setAttribute('poppy-state', 'dragging');
                }

                // Clone the drag start matrix
                let transformMatrix = {...self.options.__drag.startMatrix };
                
                // Limit the distance so we we will not move the notification out of bounds
                distance = (self.positioning.right || (self.positioning.bottom && self.positioning.center)) ? Math.max(0, distance) : Math.min(0, distance);

                // Modify the matrix according to notification's positioning
                // If notification is on the corners we will move X axis if it's on the center we will move Y axis
                if (self.positioning.right || self.positioning.left) {
                    transformMatrix.translatex += distance;
                } else {
                    transformMatrix.translatey += distance
                }

                // Convert matrix into transform string and apply it to notification
                self.options.element.style.transform = self.matrixToTransformString(transformMatrix);

            }
        }, true);

        document.documentElement.addEventListener('mouseup', function(event) {
            if (self.flags.isMouseDown) {

                // If dragging is complete we can add the transition animation back
                self.options.element.classList.remove(self.class('hasNoAnimation'));

                let width = self.options.element.offsetWidth;
                let height = self.options.element.offsetHeight;

                // Our threshold to dismiss to notification is 40 percent of the size according to positioning
                // Maybe we can put a fixed size for it so it will not be harder for bigger notifications to drag-dismissal
                let threshold = (self.positioning.center ? height : width) * 0.4;

                let distance = self.positioning.center ? event.clientY - self.options.__drag.startY : event.clientX - self.options.__drag.startX;
                let time = (new Date()).getTime() - self.options.__drag.startTime;
                let speed =  distance / time; // calculate the pixels per milliseconds (p/ms)

                // Reverse the values for left and top center sides
                // Because we are calculating the dragging from left to right and top to bottom
                // When we need the opposite sides' results we need the reverse them for correct values
                if (self.positioning.left || (self.positioning.center && self.positioning.top)) {
                    distance = 0 - distance;
                    speed = 0 - speed;
                }

                // Determine if user's dragging was enough for dismissal
                if (speed > 0.85 || distance >= threshold) {

                    // Clone the drag start matrix
                    let transformMatrix = {...self.options.__drag.startMatrix };
                    
                    // Calculate where the notification should move for dismissal
                    if (self.positioning.right) {
                        transformMatrix.translatex += width + parseFloat(window.getComputedStyle(self.options.element).right);
                    } else if (self.positioning.left) {
                        transformMatrix.translatex -= width + parseFloat(window.getComputedStyle(self.options.element).left);
                    } else if (self.positioning.center && self.positioning.top) {
                        transformMatrix.translatey -= height + parseFloat(window.getComputedStyle(self.options.element).top);
                    } else if (self.positioning.center && self.positioning.bottom) {
                        transformMatrix.translatey += height + parseFloat(window.getComputedStyle(self.options.element).bottom);
                    }

                    // Convert the matrix into transform string and apply it
                    self.options.element.style.transform = self.matrixToTransformString(transformMatrix);
                    // And dismiss it as usual
                    self.dismiss();
                } else {
                    // Set notification state
                    self.options.element.setAttribute('poppy-state', 'returning');
                    // If dragging is not sufficent for dismissal set it back to its original position
                    self.options.element.style.transform = self.matrixToTransformString(self.options.__drag.startMatrix);

                    setTimeout(function() {
                        self.options.element.removeAttribute('poppy-state');
                    }, self.getDismissDuration());
                }

                self.flags.isDragging = false;
                self.flags.isMouseDown = false;
                // Delete the drag starting point object
                delete self.options.__drag;
            }
        }, true);

        self.flags.hasRegisteredDragEvents = true;
    }

    registerDismissal() {
        const self = this;

        if (!this.options.duration) {
            return;
        }

        if (self.timer) {
            clearTimeout(self.timer);
        }

        self.timer = self.createDismissalTimer(this.options.duration);

        this.options.element.onmouseenter = function() {
            clearTimeout(self.timer);
        }

        this.options.element.onmouseleave = function() {
            self.timer = self.createDismissalTimer(self.options.duration);
        }
    }   

    createDismissalTimer(duration) {
        const self = this;

        return setTimeout(function() {
            self.dismiss();
        }, duration);
    }

    registerFlags() {
        this.flags = {            
            isGenerated: false,
            isVisible: false,
            isDragging: false,
            hasObserver: false,
            hasDismissed: true,
        };
    }

    registerObserver() {
        const self = this;

        let observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type == 'attributes' && mutation.attributeName == 'poppy-state') {
                    let state = self.options.element.getAttribute('poppy-state');
                    if (state == 'discard') {
                        self.flags.isMouseDown = false;
                        self.flags.isDragging = false;

                        setTimeout(function() {
                            self.options.element.removeAttribute('poppy-state');
                        }, self.getDismissDuration());
                    }
                }
            });
        });

        observer.observe(self.options.element, {
            attributes: true
        });

        this.flags.hasObserver = true;
    }

    style(key) {
        return this.options.style[key];
    }

    class(key) {
        return this.style('classNames')[key];
    }

    icons(key) {
        return this.style('icons')[key];
    }

    // Rotate Animation Handler
    getRotationDegrees() {
        let elm = this.options.element.getElementsByClassName(this.class('icon'))[0].querySelector('i');
        var matrix = window.getComputedStyle(elm).transform;
        if (matrix !== 'none') {
            var values = matrix.split('(')[1].split(')')[0].split(',');
            var a = values[0];
            var b = values[1];
            var angle = Math.round(Math.atan2(b, a) * (180/Math.PI));
        } else {
            var angle = 0;
        }
        return angle;
    }

    spin(status = true) {
        let icon = this.options.element.querySelector('.' + this.class('icon') + ' i');

        if (!icon) {
            console.error("Icon could not be found in notification.");
            return;
        }

        if (status) {
            // Clear the transition and transform values before starting the animation so it won't override the animation
            icon.style.transition = ``;
            icon.style.transform = ``;
            
            this.options.element.getElementsByClassName(this.class('icon'))[0].classList.add(this.class('isSpinning'));
        } else {

            // Calculate second per each degree of animation
            let spd = parseFloat(window.getComputedStyle(icon).animationDuration) / 360;

            let degree = this.getRotationDegrees();
            icon.style.transform = `rotate(${degree}deg)`;
            this.options.element.getElementsByClassName(this.class('icon'))[0].classList.remove(this.class('isSpinning'));

            setTimeout(() => {
                // Calculate the duration for a smooth ease-out transition
                // Duration = (Degrees left to complete the full 360 degree cycle) * (Required Second Per Each Degree)
                let left = 360 - Math.abs(degree);
                let duration = left * spd;
                duration = Math.round((duration * 0.5) * 100) / 100;

                icon.style.transition = `transform ${duration}s cubic-bezier(0.16, 1, 0.3, 1)`;
                if (degree < 0) {
                    icon.style.transform = `rotate(0deg)`;
                } else {
                    icon.style.transform = `rotate(360deg)`;
                }
            }, 10);
        }

        return this;
    }

    setOptions(message, title = null, options = {}) {
        if (typeof message == "object") {
            options = message;
        } else if (typeof message == "string" && !title) {
            options = this.merge(options || {}, { message: message });
        } else if (typeof message == "string" && typeof title == "object") {
            options = this.merge(title, { message: message });
        } else if (typeof message == "string" && typeof title == "string") {
            options = this.merge(options || {}, { title: title, message: message});
        }

        this.options = this.merge(this.defaults(), options)
        this.setContainer();
    }

    merge(target, source) {
        for (const key of Object.keys(source)) {
          if (source[key] instanceof Object) Object.assign(source[key], this.merge(target[key], source[key]))
        }

        Object.assign(target || {}, source)
        return target
    }
}

export default Poppy;